import type { DocMigration } from "../docMigration";

type StructuralFloorTypeValue = "FRAMING" | "SLAB";

const builtInFramingComponentsGroupId = "builtInFramingComponentsGroup";
const builtInFramingComponentsGroupName = "Dimensional Lumber";
const iJoistComponentsGroupId = "iJoistComponentsGroup";

const builtInIJoistComponents: Record<string, any> = {
  defaultIJ_9_5_2x3: {
    entityType: "FRAMING_COMPONENT",
    id: "defaultIJ_9_5_2x3",
    sortIndex: 0,
    type: "I_JOIST",
    iJoistProfileType: "IJ_9_5_2X3",
    name: 'I-Joist 9 1/2" / 2x3',
  },
  defaultIJ_11_875_2x3: {
    entityType: "FRAMING_COMPONENT",
    id: "defaultIJ_11_875_2x3",
    sortIndex: 0,
    type: "I_JOIST",
    iJoistProfileType: "IJ_11_875_2X3",
    name: 'I-Joist 11 7/8" / 2x3',
  },
  defaultIJ_11_875_2x4: {
    entityType: "FRAMING_COMPONENT",
    id: "defaultIJ_11_875_2x4",
    sortIndex: 0,
    type: "I_JOIST",
    iJoistProfileType: "IJ_11_875_2X4",
    name: 'I-Joist 11 7/8" / 2x4',
  },
  defaultIJ_14_2x3: {
    entityType: "FRAMING_COMPONENT",
    id: "defaultIJ_14_2x3",
    sortIndex: 0,
    type: "I_JOIST",
    iJoistProfileType: "IJ_14_2X3",
    name: 'I-Joist 14" / 2x3',
  },
  defaultIJ_14_2x4: {
    entityType: "FRAMING_COMPONENT",
    id: "defaultIJ_14_2x4",
    sortIndex: 0,
    type: "I_JOIST",
    iJoistProfileType: "IJ_14_2X4",
    name: 'I-Joist 14" / 2x4',
  },
  defaultIJ_16_2x3: {
    entityType: "FRAMING_COMPONENT",
    id: "defaultIJ_16_2x3",
    sortIndex: 0,
    type: "I_JOIST",
    iJoistProfileType: "IJ_16_2X3",
    name: 'I-Joist 16" / 2x3',
  },
  defaultIJ_16_2x4: {
    entityType: "FRAMING_COMPONENT",
    id: "defaultIJ_16_2x4",
    sortIndex: 0,
    type: "I_JOIST",
    iJoistProfileType: "IJ_16_2X4",
    name: 'I-Joist 16" / 2x4',
  },
  defaultIJ_18_2x4: {
    entityType: "FRAMING_COMPONENT",
    id: "defaultIJ_18_2x4",
    sortIndex: 0,
    type: "I_JOIST",
    iJoistProfileType: "IJ_18_2X4",
    name: 'I-Joist 18" / 2x4',
  },
  defaultIJ_20_2x4: {
    entityType: "FRAMING_COMPONENT",
    id: "defaultIJ_20_2x4",
    sortIndex: 0,
    type: "I_JOIST",
    iJoistProfileType: "IJ_20_2X4",
    name: 'I-Joist 20" / 2x4',
  },
};

const iJoistComponentsGroup = {
  entityType: "GROUP",
  name: "I-Joists",
  id: iJoistComponentsGroupId,
  sortIndex: 2,
  isBuiltIn: true,
};

/**
 * Map a legacy `LumberProfileType` (stored on `FloorEdgeOverride.framingConfig.framingProfile`)
 * to the corresponding built-in `FRAMING_COMPONENT` id seeded in the doc. Returns
 * undefined for unknown values so we can drop them without throwing.
 */
function lumberProfileToBuiltInFramingComponentId(profileType: string): string | undefined {
  switch (profileType) {
    case "SPF_2X4":
      return "default2x4";
    case "SPF_2X6":
      return "default2x6";
    case "SPF_2X8":
      return "default2x8";
    case "SPF_2X10":
      return "default2x10";
    case "SPF_2X12":
      return "default2x12";
    default:
      return undefined;
  }
}

function rewriteFramingConfig(framingConfig: any): void {
  if (!framingConfig || typeof framingConfig !== "object") {
    return;
  }
  if (framingConfig.framingProfile === undefined) {
    return;
  }
  if (framingConfig.framingComponentId === undefined) {
    const componentId = lumberProfileToBuiltInFramingComponentId(framingConfig.framingProfile);
    if (componentId !== undefined) {
      framingConfig.framingComponentId = componentId;
    }
  }
  delete framingConfig.framingProfile;
}

function rewriteFloorEdgeOverridesTable(table: any): void {
  if (!table || typeof table !== "object") {
    return;
  }
  for (const override of Object.values(table)) {
    if (!override || typeof override !== "object") {
      continue;
    }
    rewriteFramingConfig((override as any).framingConfig);
  }
}

function rewriteFloorBoundaries(node: any): void {
  if (!node || typeof node !== "object") {
    return;
  }
  rewriteFloorEdgeOverridesTable(node.floorEdgeOverrides);
  rewriteFloorEdgeOverridesTable(node.floorOpeningEdgeOverrides);
}

function renameBuiltInFramingComponentsGroup(json: any): void {
  const groups = json.groups;
  if (!groups || typeof groups !== "object") {
    return;
  }
  const builtInFramingComponentsGroup = groups[builtInFramingComponentsGroupId];
  if (!builtInFramingComponentsGroup || typeof builtInFramingComponentsGroup !== "object") {
    return;
  }
  builtInFramingComponentsGroup.name = builtInFramingComponentsGroupName;
}

function mergeBuiltInIJoistComponents(json: any): void {
  const existing =
    json.framingComponents !== undefined &&
    json.framingComponents !== null &&
    typeof json.framingComponents === "object" &&
    !Array.isArray(json.framingComponents)
      ? json.framingComponents
      : {};

  json.framingComponents = {
    ...builtInIJoistComponents,
    ...existing,
  };
}

function applyBuiltInIJoistEntityGroups(json: any): void {
  const framingComponents =
    json.framingComponents !== undefined &&
    json.framingComponents !== null &&
    typeof json.framingComponents === "object" &&
    !Array.isArray(json.framingComponents)
      ? json.framingComponents
      : null;
  if (framingComponents === null) {
    return;
  }

  if (json.groups === undefined || json.groups === null || typeof json.groups !== "object") {
    json.groups = {};
  }
  if (json.groups[iJoistComponentsGroupId] === undefined) {
    json.groups[iJoistComponentsGroupId] = { ...iJoistComponentsGroup };
  }

  if (json.entityGroups === undefined || json.entityGroups === null || typeof json.entityGroups !== "object") {
    json.entityGroups = {};
  }

  for (const componentId of Object.keys(builtInIJoistComponents)) {
    if (framingComponents[componentId] === undefined) {
      continue;
    }
    const entityGroupKey = `framingComponent.${componentId}`;
    if (json.entityGroups[entityGroupKey] === undefined) {
      json.entityGroups[entityGroupKey] = iJoistComponentsGroupId;
    }
  }
}

function inferStructuralTypeFromComponentId(
  componentId: string | undefined,
  framingComponents: Record<string, any> | undefined,
  slabComponents: Record<string, any> | undefined,
): StructuralFloorTypeValue | undefined {
  if (componentId === undefined) {
    return undefined;
  }
  if (framingComponents?.[componentId] !== undefined) {
    return "FRAMING";
  }
  if (slabComponents?.[componentId] !== undefined) {
    return "SLAB";
  }
  return undefined;
}

function inferStructuralTypeFromAssemblyId(
  assemblyId: string | undefined,
  frameFloorAssemblies: Record<string, any> | undefined,
  slabFloorAssemblies: Record<string, any> | undefined,
): StructuralFloorTypeValue | undefined {
  if (assemblyId === undefined) {
    return undefined;
  }
  if (frameFloorAssemblies?.[assemblyId] !== undefined) {
    return "FRAMING";
  }
  if (slabFloorAssemblies?.[assemblyId] !== undefined) {
    return "SLAB";
  }
  return undefined;
}

function inferFloorStructuralType(
  node: any,
  frameFloorAssemblies: Record<string, any> | undefined,
  slabFloorAssemblies: Record<string, any> | undefined,
  framingComponents: Record<string, any> | undefined,
  slabComponents: Record<string, any> | undefined,
): StructuralFloorTypeValue | undefined {
  if (!node || typeof node !== "object") {
    return undefined;
  }

  const structure = typeof node.structure === "string" ? node.structure : undefined;
  const structureType = inferStructuralTypeFromComponentId(structure, framingComponents, slabComponents);
  if (structureType !== undefined) {
    return structureType;
  }

  const assemblyId = typeof node.assemblyId === "string" ? node.assemblyId : undefined;
  const assemblyType = inferStructuralTypeFromAssemblyId(assemblyId, frameFloorAssemblies, slabFloorAssemblies);
  if (assemblyType !== undefined) {
    return assemblyType;
  }

  if (node.structuralType === "FRAMING" || node.structuralType === "SLAB") {
    return node.structuralType;
  }
  return undefined;
}

function rewriteResolvedFloorAssemblyFields(
  node: any,
  frameFloorAssemblies: Record<string, any> | undefined,
  slabFloorAssemblies: Record<string, any> | undefined,
  framingComponents: Record<string, any> | undefined,
  slabComponents: Record<string, any> | undefined,
): void {
  if (!node || typeof node !== "object") {
    return;
  }
  if (node.structure === undefined) {
    return;
  }

  const structuralType = inferFloorStructuralType(
    node,
    frameFloorAssemblies,
    slabFloorAssemblies,
    framingComponents,
    slabComponents,
  );
  if (structuralType === "SLAB") {
    node.slab ??= node.structure;
    delete node.structure;
  } else if (structuralType === "FRAMING") {
    node.floorJoist ??= node.structure;
    node.rimBoard ??= node.structure;
    delete node.structure;
  }
}

const migration: DocMigration = {
  id: "20260428_20260428_ijoist_framing_and_rim_board",
  description:
    "Rename built-in framing components group to Dimensional Lumber, rename FRAMING_COMPONENT type LUMBER -> " +
    "DIMENSIONAL_LUMBER, default rimComponentId on FRAME_FLOOR_ASSEMBLY entities to their joistComponentId, " +
    "and rewrite legacy floor structure fields plus FramingConfig.framingProfile (LumberProfileType) values.",
  apply(json: any) {
    if (!json || typeof json !== "object") {
      return json;
    }

    renameBuiltInFramingComponentsGroup(json);
    mergeBuiltInIJoistComponents(json);
    applyBuiltInIJoistEntityGroups(json);

    const framingComponents: Record<string, any> | undefined = json.framingComponents;
    if (framingComponents) {
      for (const component of Object.values(framingComponents)) {
        if (!component || typeof component !== "object") {
          continue;
        }
        if ((component as any).type === "LUMBER") {
          (component as any).type = "DIMENSIONAL_LUMBER";
        }
      }
    }

    const frameFloorAssemblies: Record<string, any> | undefined = json.frameFloorAssemblies;
    if (frameFloorAssemblies) {
      for (const assembly of Object.values(frameFloorAssemblies)) {
        if (!assembly || typeof assembly !== "object") {
          continue;
        }
        const a = assembly as any;
        if (a.rimComponentId === undefined && a.joistComponentId !== undefined) {
          a.rimComponentId = a.joistComponentId;
        }
      }
    }

    const slabFloorAssemblies: Record<string, any> | undefined = json.slabFloorAssemblies;
    const slabComponents: Record<string, any> | undefined = json.slabComponents;
    const floors: Record<string, any> | undefined = json.floors;
    if (floors) {
      for (const floor of Object.values(floors)) {
        rewriteResolvedFloorAssemblyFields(
          floor,
          frameFloorAssemblies,
          slabFloorAssemblies,
          framingComponents,
          slabComponents,
        );
        rewriteFloorBoundaries(floor);
      }
    }

    return json;
  },
};

export default migration;
